<template>
  <section class="financial__booking-period" aria-labelledby="financial-booking-title">
    <div class="financial__panel">
      <div class="financial__period-heading">
        <h2 id="financial-booking-title" tabindex="-1">Booking period</h2>
        <p class="financial__period-help">Applies to Shipments and Customers. Carrier invoices keep their own dates.</p>
      </div>
      <form class="financial__load" novalidate @submit.prevent="load">
        <fieldset class="financial__load-period">
          <legend class="visually-hidden">Booking period</legend>
          <div class="financial__presets" role="group" aria-label="Booking period presets">
            <button v-for="preset in presets" :key="preset.label" class="financial__chip" type="button" :aria-pressed="from === preset.from && to === preset.to" @click="usePreset(preset.label)">
              {{ preset.label }}
            </button>
          </div>
          <div class="financial__load-dates">
            <div>
              <label for="financial-load-from">Booked from</label>
              <input
                id="financial-load-from"
                v-model="from"
                class="form-control"
                type="date"
                :max="to || today"
                :aria-describedby="loadHint ? 'financial-load-status' : undefined"
                @focus="editDate(true)"
                @blur="editDate(false)"
              />
            </div>
            <div>
              <label for="financial-load-to">Booked to</label>
              <input
                id="financial-load-to"
                v-model="to"
                class="form-control"
                type="date"
                :min="from || undefined"
                :max="today"
                :aria-describedby="loadHint ? 'financial-load-status' : undefined"
                @focus="editDate(true)"
                @blur="editDate(false)"
              />
            </div>
          </div>
        </fieldset>
        <fieldset class="financial__load-carriers" :aria-describedby="loadHint ? 'financial-load-status' : undefined">
          <legend class="financial__label">Carriers</legend>
          <div class="financial__load-checks">
            <label v-for="option in carriers" :key="option" class="financial__check">
              <input v-model="selectedCarriers" type="checkbox" :value="option" /> {{ option }}
            </label>
          </div>
        </fieldset>
        <div class="financial__load-status">
          <p id="financial-load-status" class="financial__facit" :class="`financial__facit--${loadHint ? 'hint' : facit.state}`" role="status">
            <mdicon :name="loadHint ? 'information-outline' : facit.icon" :size="18" aria-hidden="true" />
            <span>{{ loadHint || (loading ? progress || 'Asking PacSort for the shipments in this period…' : facit.text) }}</span>
          </p>
          <!-- The last load's result, next to the period it was for. -->
          <p class="financial__load-result" role="status">{{ shownResult }}</p>
        </div>
        <!-- One button that turns into Cancel while loading, so focus stays where it is. -->
        <button
          class="financial__button"
          :class="{ 'financial__button--primary': !loading && facit.state !== 'loaded' }"
          :type="loading ? 'button' : 'submit'"
          :disabled="!loading && (busy || opening || !!loadHint)"
          aria-describedby="financial-load-status"
          @click="stopLoading"
        >
          <mdicon :name="loading ? 'close-circle-outline' : 'database-refresh-outline'" :size="18" aria-hidden="true" />
          {{ loading ? 'Cancel loading' : facit.state === 'loaded' || facit.state === 'partial' ? 'Reload from PacSort' : 'Load from PacSort' }}
        </button>
      </form>

      <section v-if="!opening && (periodEntries.length || covering)" class="financial__coverage" aria-labelledby="financial-coverage-title">
        <div class="financial__panel-heading">
          <h3 id="financial-coverage-title" tabindex="-1">{{ coverageTitle }}</h3>
          <strong v-if="coverage.shipped" class="financial__coverage-percent">{{ coverage.percent }}</strong>
        </div>
        <div
          v-if="coverage.shipped"
          class="financial__coverage-bar"
          role="progressbar"
          :aria-valuenow="coverage.withCost"
          aria-valuemin="0"
          :aria-valuemax="coverage.shipped"
          :aria-valuetext="`${coverage.withCost} of ${coverage.shipped} shipped shipments have a carrier cost`"
          aria-labelledby="financial-coverage-title"
        >
          <span :style="{ width: coverage.percent }"></span>
        </div>
        <!-- Keep the shared period compact; the summary above remains visible. -->
        <details class="financial__coverage-carriers">
          <summary>Per carrier</summary>
          <div class="financial__table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">Carrier</th>
                  <th scope="col">Shipped · PacSort</th>
                  <th scope="col">Carrier cost linked</th>
                  <th scope="col">No carrier cost yet</th>
                  <th scope="col">Coverage</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in coverage.carriers" :key="item.carrier">
                  <th scope="row">{{ item.carrier }}</th>
                  <td>
                    {{ numberLabel(item.shipped) }}
                    <small v-if="!item.loaded" class="financial__attention">Not loaded from PacSort</small>
                  </td>
                  <td>{{ numberLabel(item.withCost) }}</td>
                  <td :class="{ financial__attention: item.withoutCost }">{{ numberLabel(item.withoutCost) }}</td>
                  <td>{{ item.percent }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="financial__field-hint">Per carrier, only costs from that carrier's invoices count.</p>
        </details>
      </section>
    </div>
  </section>
</template>
<script lang="ts">
import type { FinancialFacit } from '@/types/Components/FinancialLedger'
import type { FinancialShipmentLedgerPresets, FinancialShipmentLedgerCoverage } from '@/types/Components/FinancialViewState'
import type { FinancialLedgerEntry, FinancialLoadedPeriod } from '@/types/Models/Financial'
import { computed, defineComponent, onBeforeUnmount, onMounted, ref, watch, type ComputedRef, type PropType, type Ref } from 'vue'
import { FINANCIAL_ACTIVE_CARRIERS, type FinancialActiveCarrier } from '@/const/Financial'
import { financialLocalDate } from '@/services/FinancialImport'
import { financialShipmentCarriers } from '@/services/FinancialLedger'
import { financialBookingEntries } from '@/services/FinancialPeriod'

export default defineComponent({
  name: 'FinancialBookingPeriod',
  props: {
    entries: { type: Array as PropType<FinancialLedgerEntry[]>, required: true },
    busy: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    opening: { type: Boolean, default: false },
    progress: { type: String, default: '' },
    // What the last load from PacSort did, or that it was cancelled.
    result: { type: String, default: '' },
    // Periods loaded from PacSort in this workspace: the facit for what was shipped.
    loadedPeriods: { type: Array as PropType<FinancialLoadedPeriod[]>, default: () => [] },
  },
  emits: ['load', 'cancel', 'change'],
  setup(props, { emit }) {
    // Presets follow the Swedish calendar day, not UTC, and move on with the day while the page stays open.
    const localToday = (): string => financialLocalDate(new Date().toISOString())
    const today: Ref<string> = ref(localToday())
    const iso = (date: Date): string => date.toISOString().slice(0, 10)
    const presets: ComputedRef<FinancialShipmentLedgerPresets> = computed(() => {
      const [year, month, day] = today.value.split('-').map(Number)
      const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay()
      const monday = day - ((weekday + 6) % 7)
      // Weeks first: invoicing is weekly, so those are the presets used most.
      return [
        { label: 'Last week', from: iso(new Date(Date.UTC(year, month - 1, monday - 7))), to: iso(new Date(Date.UTC(year, month - 1, monday - 1))) },
        { label: 'This week', from: iso(new Date(Date.UTC(year, month - 1, monday))), to: today.value },
        { label: 'Last month', from: iso(new Date(Date.UTC(year, month - 2, 1))), to: iso(new Date(Date.UTC(year, month - 1, 0))) },
        { label: 'This month', from: iso(new Date(Date.UTC(year, month - 1, 1))), to: today.value },
        { label: 'Last 30 days', from: iso(new Date(Date.UTC(year, month - 1, day - 29))), to: today.value },
      ]
    })
    const refreshToday = (): void => {
      today.value = localToday()
    }
    // Last month stays the default period although the week presets come first.
    const initial = presets.value.find((item) => item.label === 'Last month')!
    const from: Ref<string> = ref(initial.from)
    const to: Ref<string> = ref(initial.to)
    const selectedCarriers: Ref<FinancialActiveCarrier[]> = ref([...FINANCIAL_ACTIVE_CARRIERS])
    const numberLabel = (value: number): string => value.toLocaleString('en-GB')
    const periodEntries: ComputedRef<FinancialLedgerEntry[]> = computed(() => financialBookingEntries(props.entries, { from: from.value, to: to.value, carriers: selectedCarriers.value }))
    // A load lists every shipment booked before the day it ran; bookings on that day or later need a new load.
    const completeBefore = (period: FinancialLoadedPeriod): string => financialLocalDate(period.at)
    const covers = (period: FinancialLoadedPeriod, carriers: FinancialActiveCarrier[]): boolean =>
      period.from <= from.value && period.to >= to.value && carriers.every((item) => period.carriers.includes(item))
    // The latest load from PacSort that covers the chosen period and carriers.
    const covering: ComputedRef<FinancialLoadedPeriod | null> = computed(() => [...props.loadedPeriods].reverse().find((period) => covers(period, selectedCarriers.value)) ?? null)
    // Rounded down, so one missing cost never shows as 100%.
    const percent = (part: number, whole: number): string =>
      whole ? `${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 }).format(Math.floor((part / whole) * 1000) / 10)}%` : '—'
    // How much of what PacSort shipped in the period has a carrier cost, in total and per carrier.
    const coverage: ComputedRef<FinancialShipmentLedgerCoverage> = computed(() => {
      const shipped = periodEntries.value.filter((entry) => entry.shipment.source === 'pacsort')
      const withCost = shipped.filter((entry) => entry.lines.length).length
      return {
        shipped: shipped.length,
        withCost,
        percent: percent(withCost, shipped.length),
        // Per carrier, a shipment counts as costed only by rows from that carrier's own invoices.
        carriers: FINANCIAL_ACTIVE_CARRIERS.filter((item) => selectedCarriers.value.includes(item)).map((item) => {
          const own = shipped.filter((entry) => financialShipmentCarriers(entry.shipment).includes(item))
          const linked = own.filter((entry) => entry.lines.some((line) => line.batch.carrier === item)).length
          return {
            carrier: item,
            loaded: props.loadedPeriods.some((period) => covers(period, [item])),
            shipped: own.length,
            withCost: linked,
            withoutCost: own.length - linked,
            percent: percent(linked, own.length),
          }
        }),
      }
    })
    const stamp = (value: string): string =>
      new Date(value).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Europe/Stockholm' })
    // Whether PacSort's list for the chosen period and carriers is loaded: only then is the list complete.
    const facit: ComputedRef<FinancialFacit> = computed(() => {
      if (props.opening)
        return { state: 'opening', icon: 'database-clock-outline', text: 'Checking which periods are loaded from PacSort…' }
      const period = covering.value
      if (!period)
        return {
          state: 'missing',
          icon: 'database-alert-outline',
          text: 'Not loaded from PacSort for this period yet: the list only shows shipments already known, for example from invoices.',
        }
      if (completeBefore(period) > to.value)
        return {
          state: 'loaded',
          icon: 'database-check-outline',
          text: `Loaded from PacSort ${stamp(period.at)}: every shipment booked in this period is listed.`,
        }
      return {
        state: 'partial',
        icon: 'database-clock-outline',
        text: `Loaded from PacSort ${stamp(period.at)}: shipments booked after that are not listed yet.`,
      }
    })
    // A load's result belongs to the period and carriers it was asked for, and goes once another one is chosen. A load
    // cancelled because another period was chosen therefore says so only under the period it was for.
    const choice: ComputedRef<string> = computed(() => [from.value, to.value, ...[...selectedCarriers.value].sort()].join('|'))
    let requested = ''
    const resultChoice: Ref<string> = ref('')
    watch(
      () => props.result,
      (value) => {
        if (value) resultChoice.value = requested
      },
    )
    const shownResult: ComputedRef<string> = computed(() => (resultChoice.value === choice.value ? props.result : ''))
    const coverageTitle: ComputedRef<string> = computed(() => {
      const { shipped, withCost } = coverage.value
      if (shipped)
        return `${numberLabel(withCost)} of ${numberLabel(shipped)} shipped ${shipped === 1 ? 'shipment has' : 'shipments have'} a carrier cost`
      return covering.value ? 'PacSort shipped nothing in this period with the selected carriers' : "PacSort's list for this period is not loaded yet"
    })
    const loadHint: ComputedRef<string> = computed(() => {
      if (!from.value || !to.value) return 'Choose both booking dates.'
      if (from.value > to.value) return 'The start date must be on or before the end date.'
      if (to.value > today.value) return 'The end date cannot be after today.'
      // Also keeps a half-typed year (0202-…) from asking PacSort for centuries of shipments.
      if (Date.parse(to.value) - Date.parse(from.value) > 366 * 86400000) return 'Choose at most one year at a time.'
      if (!selectedCarriers.value.length) return 'Select at least one carrier.'
      return ''
    })
    // Choosing a period or carriers asks PacSort for what was shipped in it, unless that is already loaded. A date
    // being typed gets longer to settle, and a load for a period that is no longer chosen is cancelled.
    let autoTimer = 0
    let waiting = false
    let editing = false
    const loadedRecently = (): boolean =>
      facit.value.state === 'partial' && !!covering.value && Date.now() - Date.parse(covering.value.at) < 10 * 60 * 1000
    const autoLoad = (): void => {
      if (loadHint.value || facit.value.state === 'loaded' || loadedRecently()) return
      if (props.loading || props.busy || props.opening) {
        waiting = true
        return
      }
      load()
    }
    const editDate = (active: boolean): void => {
      editing = active
      if (active) refreshToday()
    }
    watch([from, to, selectedCarriers], () => {
      if (props.loading) emit('cancel')
      window.clearTimeout(autoTimer)
      autoTimer = window.setTimeout(autoLoad, editing ? 1500 : 500)
    })
    watch(
      () => props.loading || props.busy || props.opening,
      (working) => {
        if (working || !waiting) return
        waiting = false
        autoLoad()
      },
    )
    const usePreset = (label: string): void => {
      refreshToday()
      const preset = presets.value.find((item) => item.label === label)
      if (!preset) return
      from.value = preset.from
      to.value = preset.to
    }
    const load = (): void => {
      window.clearTimeout(autoTimer)
      waiting = false
      if (loadHint.value) return
      requested = choice.value
      emit('load', { from: from.value, to: to.value, carriers: [...selectedCarriers.value] })
    }
    const stopLoading = (): void => {
      if (!props.loading) return
      window.clearTimeout(autoTimer)
      waiting = false
      emit('cancel')
    }
    watch([from, to, selectedCarriers], () => {
      emit('change', { from: from.value, to: to.value, carriers: [...selectedCarriers.value] })
    }, { immediate: true })
    onMounted(() => {
      document.addEventListener('visibilitychange', refreshToday)
      window.addEventListener('focus', refreshToday)
    })
    onBeforeUnmount(() => {
      window.clearTimeout(autoTimer)
      document.removeEventListener('visibilitychange', refreshToday)
      window.removeEventListener('focus', refreshToday)
    })
    return { today, presets, from, to, selectedCarriers, carriers: FINANCIAL_ACTIVE_CARRIERS, periodEntries, covering,
      coverage, coverageTitle, facit, shownResult, loadHint, editDate, stopLoading, numberLabel, usePreset, load }
  },
})
</script>
